/**
 * Ms. Marvel — „Unmatched: Teen Spirit".
 *
 * STRETCHY: na začiatku ťahu sa smie posunúť o 1 a útočí až na 2 priestory
 * (bez ohľadu na zóny — `ATTACK_REACH` v movement.ts). Veľa kariet sa pýta, či
 * „nezdieľa zónu so súperovou figúrkou", teda či útočí z diaľky.
 */

import { neighbors, shareZone, zonesOf } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, drawCard, emit, gainAction, getMap, getPlayer, healFighter } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex } from '../types.js';
import { disengage, feint, momentousShift } from './common.js';
import { askBoost, canMove, fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const MARVEL = 'ms-marvel';
const FRIENDS = 'ms-marvel.friends-and-family';
const SHRINK = 'ms-marvel.shrink-shrink-shrink';

/** Počet akcií v ťahu — `ACTIONS_PER_TURN` z game.ts sa sem importovať nedá (kruh). */
const ACTIONS_PER_TURN = 2;

const marvelOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === MARVEL) ?? null;

/** „If Ms. Marvel's space shares no zones with the opposing fighter" — porazený súper sa neráta. */
function apart(e: EffectCtx): boolean {
  const me = marvelOf(e.ctx, e.player);
  const them = fighterOrNull(e.ctx, e.opponentFighterId);
  return Boolean(me && them && !shareZone(getMap(e.ctx), me.spaceId, them.spaceId));
}

/** Karty v ruke. Zahrávaná schéma v nej ešte leží, preto ju odrátaj (`playing`). */
const handSize = (ctx: Ctx, player: PlayerIndex, playing = false) =>
  getPlayer(ctx.state, player).hand.length - (playing ? 1 : 0);

/** „If Ms. Marvel's space shares no zones with the opposing fighter, you may BOOST this card." */
const bigWindUp: CardEffect = {
  duringChoice: (e) => {
    if (apart(e)) askBoost(e, 'Big Wind-Up: smieš boostovať (útočíš z inej zóny)');
  },
};

/** „Draw 1 card. Then, if you have 4 or more cards in your hand, deal 1 damage to an adjacent fighter." */
const easyPeasy: CardEffect = {
  after: (e) => {
    drawCard(e.ctx, e.player);
    const me = marvelOf(e.ctx, e.player);
    if (!me || handSize(e.ctx, e.player) < 4) return;
    const around = new Set(neighbors(getMap(e.ctx), me.spaceId));
    const targets = e.ctx.state.fighters
      .filter((f) => around.has(f.spaceId) && areEnemies(e.ctx.state, f.owner, e.player))
      .map((f) => f.id);
    if (targets.length === 0) return;
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Easy-Peasy: 1 poškodenie susednej figúrke',
      optional: false,
      amount: 1,
      targets,
    });
  },
};

/** „If Ms. Marvel is in more zones than the opposing fighter, the value of this card is 6 instead." */
const embiggen: CardEffect = {
  during: (e, value) => {
    const me = marvelOf(e.ctx, e.player);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!me || !them) return value;
    const map = getMap(e.ctx);
    return zonesOf(map, me.spaceId).length > zonesOf(map, them.spaceId).length ? 6 : value;
  },
};

/**
 * „Draw 2 cards. Then, you may spend an additional action to draw until you
 * have 7 cards in your hand." Akcia navyše sa minie ako ďalšia schéma.
 */
const friendsAndFamily: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    const turn = e.ctx.state.turn;
    if (turn.player !== e.player) return;
    if (turn.actionsUsed.length >= ACTIONS_PER_TURN + (turn.extraActions ?? 0)) return;
    // Karta je ešte v ruke — po odhodení bude o 1 menej.
    if (handSize(e.ctx, e.player, true) >= 7) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Friends and Family: minúť ďalšiu akciu a doťahovať do 7 kariet?',
      optional: true,
      effect: FRIENDS,
      yes: 'Minúť akciu a ťahať',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const turn = ctx.state.turn;
    if (turn.actionsUsed.length >= ACTIONS_PER_TURN + (turn.extraActions ?? 0)) return;
    turn.actionsUsed.push('scheme');
    emit(ctx, { type: 'actionUsed', player: choice.player, kind: 'scheme' });
    // Prázdny balíček ťahanie zastaví (každý pokus by inak zraňoval).
    while (handSize(ctx, choice.player) < 7 && getPlayer(ctx.state, choice.player).deck.length > 0) {
      drawCard(ctx, choice.player);
    }
  },
};

/** „Recover 2 health. Then, if you have 4 or more cards in your hand, gain 1 action." */
const gyroAndFries: CardEffect = {
  scheme: (e) => {
    const me = marvelOf(e.ctx, e.player);
    if (me) healFighter(e.ctx, me.id, 2);
    if (handSize(e.ctx, e.player, true) >= 4) gainAction(e.ctx, e.player);
  },
};

/** „If Ms. Marvel's space shares no zones with the opposing fighter, draw 2 cards." */
const imNotTouchingYou: CardEffect = {
  after: (e) => {
    if (!apart(e)) return;
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/**
 * „Choose one effect. If Ms. Marvel's space shares no zones with the opposing
 * fighter, do both: your opponent discards 1 card / move the opposing fighter
 * up to 3 spaces."
 */
const shrinkShrinkShrink: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    const target = e.opponentFighterId;
    const discard = opponent !== null && getPlayer(e.ctx.state, opponent).hand.length > 0 ? `discard:${opponent}` : null;
    const move = target && canMove(e.ctx, target, 3, false) ? `move:${target}` : null;
    const follow = (data: string) => ({ kind: 'effect' as const, effect: SHRINK, data });
    if (apart(e)) {
      for (const data of [discard, move]) {
        if (!data) continue;
        askChoice(e.ctx, { kind: 'auto', player: e.player, source: e.cardId, label: 'Shrink Shrink Shrink', optional: false, follow: follow(data) });
      }
      return;
    }
    const options = [
      ...(discard ? [{ label: 'Súper odhodí 1 kartu', follow: follow(discard) }] : []),
      ...(move ? [{ label: 'Presuň súperovu figúrku až o 3', follow: follow(move) }] : []),
    ];
    if (options.length === 0) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Shrink Shrink Shrink: vyber jeden efekt',
      optional: false,
      options,
    });
  },
  onFollow: (ctx, choice, data) => {
    const [what, id] = data.split(':') as [string, string];
    if (what === 'discard') {
      const player = Number(id);
      if (getPlayer(ctx.state, player).hand.length === 0) return;
      askChoice(ctx, {
        kind: 'card',
        player,
        source: choice.source,
        label: 'Súperov efekt: odhoď 1 kartu z ruky',
        optional: false,
        from: 'hand',
        purpose: 'discard',
      });
      return;
    }
    if (!canMove(ctx, id, 3, false)) return;
    askChoice(ctx, {
      kind: 'move',
      player: choice.player,
      source: choice.source,
      label: 'Shrink Shrink Shrink: presuň súperovu figúrku až o 3',
      optional: true,
      fighters: [id],
      max: 3,
      through: false,
    });
  },
};

/** „The value of this card is equal to the number of cards in your hand." */
const fangirl: CardEffect = {
  during: (e) => handSize(e.ctx, e.player),
};

export const msMarvelCards: EffectRegistry = {
  'ms-marvel.big-wind-up': bigWindUp,
  'ms-marvel.easy-peasy': easyPeasy,
  'ms-marvel.embiggen': embiggen,
  [FRIENDS]: friendsAndFamily,
  'ms-marvel.gyro-and-fries': gyroAndFries,
  'ms-marvel.im-not-touching-you': imNotTouchingYou,
  [SHRINK]: shrinkShrinkShrink,
  // „Place Ms. Marvel in any space in her zone." — prázdny, inak by sa na neho nedalo postaviť.
  'ms-marvel.slingshot': disengage,
  'ms-marvel.fangirl': fangirl,
  'ms-marvel.feint': feint,
  'ms-marvel.momentous-shift': momentousShift,
};

export const msMarvelSpecial: HeroSpecial = {
  name: 'Stretchy',
  /** „At the start of your turn, you may move Ms. Marvel 1 space." */
  startOfTurn: (ctx, player) => {
    const me = marvelOf(ctx, player);
    if (!me || !canMove(ctx, me.id, 1, false)) return;
    askChoice(ctx, {
      kind: 'move',
      player,
      source: null,
      label: 'Stretchy: smieš posunúť Ms. Marvel o 1',
      optional: true,
      fighters: [me.id],
      max: 1,
      through: false,
    });
  },
};
